/**
 * Display vocabulary, in one place.
 *
 * This used to live inside Rezervace.tsx. It moved here the moment a second
 * screen (the reservation detail) needed the same words, because the one
 * thing that must never drift in this application is the label a dispatcher
 * reads. If the grid says "Nedorazil" and the detail says "noshow", the two
 * screens are describing the same booking in two languages and somebody will
 * eventually act on the wrong one.
 *
 * Every label here is the legacy extranet's own wording. Where the legacy
 * screen showed a raw database value — the parking spot codes did exactly
 * that — the catalogue in @smile/shared supplies the human form instead.
 *
 * The pure, harness-tested helpers (dates, URL filters, e-mail and phone
 * plausibility) live in ./filters.ts, which imports nothing.
 */
import { EUROPORT_SPOTS, type ReservationRow, type UserRole } from '@smile/shared';

export const CZK = new Intl.NumberFormat('cs-CZ', { maximumFractionDigits: 0 });

/**
 * One decimal place, Czech-style — '78,8', not '78.8'.
 *
 * Every number this application prints goes through Intl so the thousands
 * separator and the decimal comma are the ones a Czech reader expects.
 * `toFixed()` is the easy way to break that for one screen, so the share
 * column has a formatter of its own rather than an exception.
 */
export const PCT = new Intl.NumberFormat('cs-CZ', {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});

/**
 * `Reservation::get()`'s `$statusy`, the words the old extranet used for a
 * booking's state, gender slips and all — a dispatcher who has read
 * "Propadlá" for thirteen years does not need to learn "Nedorazil".
 */
export const STATUS_LABEL: Record<string, string> = {
  new: 'Nová',
  pending: 'Připravena',
  verified: 'Aktivní (parkuje)',
  paid: 'Předplaceno',
  closed: 'Uzavřena',
  canceled: 'Zrušeno',
  noshow: 'Propadlá',
  deleted: 'Smazáno',
};

/**
 * The legacy Stav filter's option list (page.reservations.html), in its
 * order: three groups, then "Pouze:" one status each. Keys are the values
 * page.reservations.php switched on and buildReservationWhere reproduces.
 */
export const STATUS_FILTER_OPTIONS: ReadonlyArray<{ value: string; label: string; group?: true }> = [
  { value: 'active', label: 'Živé', group: true },
  { value: 'waiting', label: 'Příjezdy', group: true },
  { value: 'notdeleted', label: 'Bez storen, smazaných a propadlých', group: true },
  { value: 'new', label: 'Nové' },
  { value: 'paid', label: 'Předplacené' },
  { value: 'verified', label: 'Aktivní (parkují)' },
  { value: 'noshow', label: 'Propadlé' },
  { value: 'closed', label: 'Uzavřené' },
  { value: 'canceled', label: 'Zrušené' },
  { value: 'deleted', label: 'Smazané' },
];

/** The legacy Parking filter: Venkovní / Garážové / VIP. */
export const TYPE_FILTER_LABEL: Record<string, string> = {
  cheap: 'Venkovní',
  regular: 'Garážové',
  exclusive: 'VIP',
};

/** `$typy_short`: the word the Kde cell carries beside its icon. */
export const TYPE_SHORT: Record<string, string> = {
  cheap: 'Venku',
  regular: 'Garáž',
  exclusive: 'Europort VIP',
};

/** The legacy Smile Club filter's options, in its order. */
export const CLUB_FILTER_OPTIONS: ReadonlyArray<{ value: string; label: string; group?: true }> = [
  { value: 'member', label: 'Členi', group: true },
  { value: 'notmember', label: 'Ne členi', group: true },
  { value: 'notactive', label: 'Neaktivní' },
  { value: 'silver', label: 'Silver' },
  { value: 'gold', label: 'Gold' },
  { value: 'platinum', label: 'Platinum' },
];

/** The legacy Web (zdroj) filter's options, in its order. */
export const SOURCE_FILTER_OPTIONS: ReadonlyArray<{ value: string; label: string; group?: true }> = [
  { value: 'smile', label: 'SmileParking.cz', group: true },
  { value: 'dovolena', label: 'DovolenaParking.cz', group: true },
  { value: 'valet', label: 'ValetParking.cz', group: true },
  { value: 'parkvia', label: 'ParkVia' },
  { value: 'ck', label: 'CK, partner' },
];

export const STATUS_CLASS: Record<string, string> = {
  new: 'stav new',
  verified: 'stav',
  paid: 'stav',
  closed: 'stav gray',
  canceled: 'stav warn',
  noshow: 'stav warn',
  deleted: 'stav gray',
};

/**
 * The three products, named as the legacy named them (`class.Reservation`
 * `$typy` / `$typy_short`): Venkovní parkování · Garážové parkování ·
 * Europort VIP parkování All Inclusive; the short forms are TYPE_SHORT above.
 * "Economy / Standard / Exclusive" was this system's own invention and the
 * owner's first question on seeing the booking form (20 October 2026).
 */
export const TYPE_LABEL: Record<string, string> = {
  cheap: 'Venkovní parkování',
  regular: 'Garážové parkování',
  exclusive: 'Europort VIP parkování All Inclusive',
};


export const PAYMENT_LABEL: Record<string, string> = {
  card: 'Karta',
  cash: 'Hotovost',
  go_pay: 'GoPay',
  bitpay: 'BitPay',
  invoice: 'Faktura',
};

export const ROLE_LABEL: Record<UserRole, string> = {
  admin: 'Administrátor',
  // The legacy's own names (class.User.php), on the owner's word of 30 October 2026.
  manager: 'Dispečer',
  operator: 'Provozní',
  driver: 'Řidič',
  partner: 'Partner',
  disabled: 'Zablokovaný',
  deleted: 'Smazaný',
};

export const LOCATION_LABEL: Record<string, string> = {
  airport: 'Europort',
  tuchomerice: 'Tuchoměřice',
  // The enum has a third member and not one row uses it. It is listed anyway
  // so a breakdown chart can never print a raw database word at a manager.
  longterm: 'Dlouhodobé',
};

/**
 * Club tier, including the empty key for a booking made by somebody who is not
 * a member. Silver is the default tier and earns no badge in a grid — see
 * CLUB_TIERS in @smile/shared — but a breakdown has to name every slice.
 */
export const TIER_LABEL: Record<string, string> = {
  '': 'Nečlen',
  silver: 'Silver',
  gold: 'Gold',
  platinum: 'Platinum',
};

/**
 * `reservation.source` — which of the three front-end brands took the booking.
 * The anonymised staging copy has one value on all 97,902 rows, so a breakdown
 * by source is a single bar there; production is expected to differ, and the
 * approved dashboard mockup's "Valet" legend suggests it does.
 */
export const SOURCE_LABEL: Record<string, string> = {
  smileparking: 'SmileParking.cz',
  dovolenaparking: 'DovolenáParking.cz',
  valetparking: 'ValetParking.cz',
};

/** `$language_flag`: the two-letter flag file the Klient cell showed. */
export const LANGUAGE_FLAG: Record<string, string> = {
  cs: 'cz',
  en: 'gb',
  ru: 'ru',
  de: 'de',
  ko: 'ko',
};

/**
 * The extra-service icon of the legacy Klient cell, by catalogue id — the
 * old extranet's own icons (its Flaticon font, `public/flaticon.woff`, the
 * same glyphs the staff have read for years; the owner, 26 October 2026:
 * "the icons have to make sense, according to the service"). The legacy
 * template: 1 cleaning → `clean` (the brush), 3 fuel → `fuel3` (the pump),
 * 7 exterior wash → `silhouette3` (the car wash), 6 shampooing → `droplet1`
 * (the drop), 2 valet → the plane, anything else a plus. The titles are the
 * catalogue as it stands on production (ids 1, 7, 16, 17, 18 live; 3, 5, 6
 * deleted but still on old bookings).
 */
export type ServiceIcon = { icon: 'clean' | 'fuel3' | 'silhouette3' | 'droplet1' | null; glyph: string; title: string };
export function serviceGlyph(id: number): ServiceIcon {
  switch (id) {
    case 2:
      return { icon: null, glyph: '✈', title: 'Valet parking (odvoz a přistavení vozidla na letiště)' };
    case 1:
      return { icon: 'clean', glyph: '🧹', title: 'Čištění interiéru vozidla (osobní vůz)' };
    case 17:
      return { icon: 'clean', glyph: '🧹', title: 'Čištění interiéru vozidla (dodávka atd.)' };
    case 7:
      return { icon: 'silhouette3', glyph: '🚿', title: 'Mytí zvenku (osobní vůz)' };
    case 18:
      return { icon: 'silhouette3', glyph: '🚿', title: 'Mytí zvenku (dodávka)' };
    case 14:
      return { icon: 'silhouette3', glyph: '🚿', title: 'Venkovní mytí' };
    case 16:
      return { icon: null, glyph: '🔌', title: 'Nabíjení elektromobilu v Tuchoměřicích' };
    case 3:
      return { icon: 'fuel3', glyph: '⛽', title: 'Dotankování vozidla' };
    case 5:
    case 6:
    case 13:
      return { icon: 'droplet1', glyph: '💧', title: 'Tepování' };
    default:
      return { icon: null, glyph: '+', title: `Služba #${id}` };
  }
}

const EUROPORT_LABEL = new Map<string, string>(
  EUROPORT_SPOTS.map((s) => [s.value as string, s.label as string]),
);

/**
 * The parking column stores form values, not words: 'outside_exit', 'M', '8'.
 * The legacy grid printed them raw and long-serving staff learned them; new
 * staff should not have to. Tuchoměřice spots are already stored as human
 * names, so they pass through untouched.
 */
export function spotLabel(spot: string | null | undefined): string {
  if (!spot) return '';
  return EUROPORT_LABEL.get(spot) ?? spot;
}

/**
 * Is this parking value one of the ten Europort spots?
 *
 * The two lots are told apart by the spot itself rather than by
 * `parking_location`, because on the transfer queue the question being asked
 * is "where is the car standing right now", and the spot is the column that
 * is actually filled in by the person who parked it.
 */
export function isEuroportSpot(spot: string | null | undefined): boolean {
  return !!spot && EUROPORT_LABEL.has(spot);
}

/**
 * Row tint, preserved from the legacy grid because dispatchers read the
 * table by colour before they read it by text:
 *   cream  — fully paid
 *   lilac  — created in the last 24 h
 */
export function rowClass(r: ReservationRow): string {
  const cls: string[] = ['click'];
  if (r.outstanding <= 0 && r.price > 0) cls.push('paid');
  if (r.created) {
    const age = Date.now() - new Date(r.created.replace(' ', 'T')).getTime();
    if (age >= 0 && age < 86_400_000) cls.push('recent');
  }
  return cls.join(' ');
}

/**
 * The row icon from the legacy dashboard's legend.
 *
 * The legend is the one on the approved mockup, with a single honest change:
 * ◎ is described there as "Předplacená (GoPay)", but a reservation row carries
 * no payment timestamp and no reliable method on most rows, so the icon here
 * means "money is already in" whatever route it came by. Claiming GoPay
 * specifically would be a guess printed as a fact.
 *
 * Cancelled and lapsed states win over everything else: a cancelled booking
 * that happens to be paid is still cancelled, and showing it as prepaid is how
 * a car gets fetched for a customer who is not coming.
 */
export function statusIcon(r: ReservationRow): string {
  if (r.status === 'closed') return '✔';
  if (r.status === 'canceled' || r.status === 'deleted') return '⊘';
  if (r.status === 'noshow') return '❗';

  const parked = !!r.arrivalReal && !r.departureReal;
  const prepaid = r.price > 0 && r.paid > 0 && r.outstanding <= 0;
  if (parked && prepaid) return '🅿+◎';
  if (parked) return '🅿';
  if (prepaid) return '◎';
  return '🔥';
}

/**
 * The "Kde" column: flight, partner traffic, and garage-vs-outside.
 *
 * 🏠 / 🅐 come off the parking spot, not off `parkingLocation` — the legend
 * word is "Garáž / Venku", and the two outside spots at Europort are stored as
 * 'outside_entry' and 'outside_exit'. A car with no spot recorded yet gets
 * neither icon rather than a guessed one.
 */
export function placeIcons(r: ReservationRow): string {
  const out: string[] = [];
  if (r.partnerRef) out.push('🚩');
  if (r.flightNumber) out.push('✈');
  if (r.parking) out.push(r.parking.startsWith('outside') ? '🅐' : '🏠');
  return out.join(' ');
}

/**
 * Czech noun agreement. The rule itself is in ./filters, which imports
 * nothing and is therefore executed by `npm run verify:web`; the three words
 * this application actually counts are named here, with the rest of its
 * display vocabulary.
 */
export { plural } from './filters';
import { plural } from './filters';

/** 1 den, 2–4 dny, 5+ dní. */
export function dayWord(n: number): string {
  return plural(n, 'den', 'dny', 'dní');
}

/** 1 rezervace, 2–4 rezervace, 5+ rezervací. */
export function rezWord(n: number): string {
  return plural(n, 'rezervace', 'rezervace', 'rezervací');
}

/** 1 záznam, 2–4 záznamy, 5+ záznamů. */
export function zaznamWord(n: number): string {
  return plural(n, 'záznam', 'záznamy', 'záznamů');
}

/**
 * 1 člen, 2–4 členové, 5+ členů.
 *
 * The `few` form is the one worth reading twice. `člen` is an animate
 * masculine noun, and animate masculines take -ové in the nominative plural
 * rather than the -i/-y a mechanical rule would produce: dva členové, not
 * "dva členi". Getting this wrong is not a typo an operator forgives — it is
 * the difference between a screen written by somebody who speaks the language
 * and one that was translated.
 *
 * The genitive plural, which is what 0 and 5+ take, is regular: členů.
 */
export function clenWord(n: number): string {
  return plural(n, 'člen', 'členové', 'členů');
}
